import { AxiosResponse } from 'axios';
import i18next from 'i18next';
import { hideLoading, showLoading } from 'react-redux-loading-bar';
import { go, push, replace } from 'react-router-redux';
import { put, select } from 'redux-saga/effects';
import { RootState, store } from 'src/app/store';
import CheckCircleBig from 'src/assets/check-circle-big.svg';
import {
  ApiPaginationResponse,
  TableQueryParameters,
} from 'src/components/ui/Table/Table.interface';
import { formatListTickets, getTicketId } from 'src/utils/ticketUtils';
import { cleanQueryParams } from 'src/utils/useQueryParams';

import ticketCreatedImg from '../../assets/ticket-created.svg';
import instance, { CustomAxiosError } from '../../axios';
import { apiBaseRoutes, apiRoutes } from '../../constants/apiConstants';
import { Errors } from '../../constants/errorConstants';
import {
  addError,
  addModal,
  addSnackbar,
  removeModal,
  setGlobalLoading,
  startLoading,
  stopLoading,
} from '../../features/globalSlice';
import { routes } from '../../routes';
import {
  accessTicketPIN,
  addTicketNote,
  assignTicket,
  createTicket,
  getPriorityTickets,
  getTicketDetails,
  getTicketList,
  getTicketNotes,
  getTicketStatistics,
  reassignTicket,
  resolveTicket,
  setPriorityTickets,
  setTicketDetails,
  setTicketList,
  setTicketNotes,
  setTicketStatistics,
} from '../actions/ticketActions';
import { Role } from '../models/Auth';
import { ModalType } from '../models/Modal';
import { SnackbarType } from '../models/Snackbar';
import {
  ListTicket,
  Ticket,
  TicketNote,
  TicketStatistics,
  TicketStatus,
} from '../models/Ticket';
import { ApiResponse } from '.';

/* Get ticket list */
export function* TicketGetTicketListSaga(
  action: ReturnType<typeof getTicketList>
): Generator {
  yield put(showLoading());
  yield put(startLoading({ actionType: action.type }));
  try {
    const state = (yield select()) as RootState;

    const paramsString = cleanQueryParams<
      TableQueryParameters<TicketStatus, ListTicket>
    >(action.payload.params);

    const endpoint =
      state?.auth?.user?.role === Role.SERVICE
        ? `${apiBaseRoutes.SERVICE}${apiRoutes.TICKETS}/${action.payload.manId}`
        : `${apiBaseRoutes.MANUFACTURER}${apiRoutes.TICKETS}`;

    const response = (yield instance.get(
      `${endpoint}${paramsString}`
    )) as AxiosResponse<ApiPaginationResponse<Ticket[], TicketStatus>>;

    yield put(
      setTicketList({
        ...response.data,
        data: formatListTickets(response.data?.data),
      })
    );
  } catch (e) {
    const error = e as CustomAxiosError;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: error.response?.data?.message || Errors.UNKNOWN_ERROR,
        snackKey: Date.now(),
      })
    );
  } finally {
    yield put(hideLoading());
    yield put(stopLoading({ actionType: action.type }));
  }
}

/* Get priority tickets */
export function* TicketGetPriorityTicketsSaga(
  action: ReturnType<typeof getPriorityTickets>
): Generator {
  yield put(showLoading());
  yield put(startLoading({ actionType: action.type }));
  try {
    const state = (yield select()) as RootState;
    const isService = state?.auth?.user?.role === Role.SERVICE;

    const paramsString = cleanQueryParams<TableQueryParameters<TicketStatus>>({
      size: 3,
      s: isService ? TicketStatus.TO_DO : TicketStatus.UNASSIGNED,
    });

    const endpoint =
      state?.auth?.user?.role === Role.SERVICE
        ? `${apiBaseRoutes.SERVICE}${apiRoutes.TICKETS}/${state.auth.user?.company?.manufacturerId}`
        : `${apiBaseRoutes.MANUFACTURER}${apiRoutes.TICKETS}`;

    const response = (yield instance.get(
      `${endpoint}${paramsString}`
    )) as AxiosResponse<ApiResponse<Ticket[]>>;

    yield put(setPriorityTickets(formatListTickets(response.data?.data)));
  } catch (e) {
    const error = e as CustomAxiosError;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: error.response?.data?.message || Errors.UNKNOWN_ERROR,
        snackKey: Date.now(),
      })
    );
  } finally {
    yield put(hideLoading());
    yield put(stopLoading({ actionType: action.type }));
  }
}

/* Get ticket details */
export function* TicketGetTicketDetailsSaga(
  action: ReturnType<typeof getTicketDetails>
): Generator {
  yield put(showLoading());
  yield put(startLoading({ actionType: action.type }));
  try {
    const state = (yield select()) as RootState;

    const endpoint =
      state?.auth?.user?.role === Role.SERVICE
        ? apiBaseRoutes.SERVICE
        : apiBaseRoutes.MANUFACTURER;

    const response = (yield instance.get(
      `${endpoint}${apiRoutes.TICKET}/${action.payload.id}`
    )) as AxiosResponse<ApiResponse<Ticket>>;

    const ticket = response.data?.data;
    yield put(
      setTicketDetails({
        ...ticket,
        ticketId: getTicketId(ticket?.prefix, ticket?.id),
      })
    );
  } catch (e) {
    const error = e as CustomAxiosError;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: error.response?.data?.message || Errors.UNKNOWN_ERROR,
        snackKey: Date.now(),
      })
    );
  } finally {
    yield put(hideLoading());
    yield put(stopLoading({ actionType: action.type }));
  }
}

/* Create ticket */
export function* TicketCreateTicketSaga(
  action: ReturnType<typeof createTicket>
): Generator {
  yield put(setGlobalLoading(true));
  try {
    const state = (yield select()) as RootState;
    const manufacturerId = state.auth?.user?.manufacturer?.id;

    const { data } = (yield instance.post(
      `${apiBaseRoutes.SERVICE}${apiRoutes.TICKETS}/${manufacturerId}`,
      {
        title: action.payload?.title,
        eMail: action.payload?.eMail,
        address: Object.values(action.payload?.address)
          .filter((v) => v)
          .join(','),
        phone: action.payload?.phone,
        model: action.payload?.model,
        manufacturerId,
        unitId: action.payload?.unitId,
        text: action.payload?.text,
      }
    )) as AxiosResponse<ApiResponse<Ticket>>;

    yield put(
      addModal({
        id: '1',
        title: i18next.t('tickets:ticket_created'),
        body: i18next.t('tickets:ticket_created_body', {
          ticket: getTicketId(data?.data?.prefix, data?.data?.id),
        }),
        modalType: ModalType.SUCCESS,
        icon: ticketCreatedImg,
        onBackdropClose: false,
        primaryButtonText: i18next.t('ticket_details:see_details'),
        secondaryButtonText: i18next.t('tickets:goto_tickets'),
        primaryAction: () => {
          store.dispatch(
            push(`${routes.TICKETS}/${data.data.id}`, { forcePush: true })
          );
          store.dispatch(removeModal());
        },
        secondaryAction: () => {
          store.dispatch(push(routes.TICKETS, { forcePush: true }));
          store.dispatch(removeModal());
        },
      })
    );
  } catch (e) {
    const error = e as CustomAxiosError;
    const errorMessage = error.response?.data?.message || Errors.UNKNOWN_ERROR;
    if (typeof error.response?.data?.message === 'string') {
      yield put(
        addSnackbar({
          type: SnackbarType.ERROR,
          body: errorMessage,
          snackKey: Date.now(),
        })
      );
    } else {
      yield put(
        addError({
          actionType: action.type,
          error: errorMessage,
        })
      );
    }
  } finally {
    yield put(setGlobalLoading(false));
  }
}

/* Access ticket with PIN */
export function* TicketAccessTicketPINSaga(
  action: ReturnType<typeof accessTicketPIN>
): Generator {
  yield put(startLoading({ actionType: action.type }));
  try {
    yield instance.post(
      `${apiBaseRoutes.SERVICE}${apiRoutes.ACCESS}/${action.payload.ticketId}/${action.payload.unitId}`,
      {
        pin: action.payload.pin,
      }
    );

    yield put(removeModal());
    yield put(go(0));
  } catch (e) {
    yield put(
      addError({
        actionType: action.type,
        error: i18next.t('ticket_details:wrong_pin'),
      })
    );
  } finally {
    yield put(stopLoading({ actionType: action.type }));
  }
}

/* Get ticket notes */
export function* TicketGetTicketNotesSaga(
  action: ReturnType<typeof getTicketNotes>
): Generator {
  yield put(showLoading());
  yield put(startLoading({ actionType: action.type }));
  try {
    const state = (yield select()) as RootState;

    const endpoint =
      state?.auth?.user?.role === Role.SERVICE
        ? apiRoutes.NOTES
        : apiRoutes.MANUFACTURER_NOTES;

    const response = (yield instance.get(
      `${endpoint}/${action.payload.ticketId}`
    )) as AxiosResponse<ApiResponse<TicketNote[]>>;

    yield put(setTicketNotes(response?.data?.data));
  } catch (e) {
    const error = e as CustomAxiosError;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: error.response?.data?.message || Errors.UNKNOWN_ERROR,
        snackKey: Date.now(),
      })
    );
  } finally {
    yield put(hideLoading());
    yield put(stopLoading({ actionType: action.type }));
  }
}

/* Add ticket note */
export function* TicketAddTicketNoteSaga(
  action: ReturnType<typeof addTicketNote>
): Generator {
  yield put(startLoading({ actionType: action.type }));
  try {
    const state = (yield select()) as RootState;

    const response = (yield instance.post(
      `${apiRoutes.NOTES}/${action.payload.ticketId}`,
      {
        text: action.payload.text,
      }
    )) as AxiosResponse<ApiResponse<TicketNote>>;

    yield put(
      setTicketNotes([...state.ticket.ticketNotes, response?.data?.data])
    );
  } catch (e) {
    const error = e as CustomAxiosError;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: error.response?.data?.message || Errors.UNKNOWN_ERROR,
        snackKey: Date.now(),
      })
    );
  } finally {
    yield put(stopLoading({ actionType: action.type }));
  }
}

/* Resolve ticket */
export function* TicketResolveTicketSaga(
  action: ReturnType<typeof resolveTicket>
): Generator {
  yield put(startLoading({ actionType: action.type }));
  try {
    const resolveData = action?.payload?.resolveData;

    const { data } = (yield instance.put(
      `${apiBaseRoutes.SERVICE}${apiRoutes.TICKETS_RESOLVE}/${action.payload.ticketId}`,
      {
        remote: !!resolveData?.repairMethod?.[0],
        inPerson: !!resolveData?.repairMethod?.[1],
        notes: resolveData?.notes,
        checkUpDate: resolveData?.checkUpDate
          ? new Date(resolveData?.checkUpDate).toISOString()
          : undefined,
      }
    )) as AxiosResponse<ApiResponse<Ticket>>;

    yield put(
      addModal({
        title: i18next.t('common:well_done'),
        body: i18next.t('ticket_details:succesfully_resolved', {
          ticket: getTicketId(data?.data?.prefix, data?.data?.id),
        }),
        modalType: ModalType.SUCCESS,
        icon: CheckCircleBig,
        onBackdropClose: false,
        primaryButtonText: i18next.t('tickets:goto_tickets'),
        secondaryButtonText: i18next.t('ticket_details:go_to_dashboard'),
        primaryAction: () => {
          store.dispatch(push(routes.TICKETS, { forcePush: true }));
          store.dispatch(removeModal());
        },
        secondaryAction: () => {
          store.dispatch(push(routes.HOME, { forcePush: true }));
          store.dispatch(removeModal());
        },
      })
    );
  } catch (e) {
    const error = e as CustomAxiosError;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: error.response?.data?.message || Errors.UNKNOWN_ERROR,
        snackKey: Date.now(),
      })
    );
  } finally {
    yield put(stopLoading({ actionType: action.type }));
  }
}

/* Assign ticket */
export function* TicketAssignTicketSaga(
  action: ReturnType<typeof assignTicket>
): Generator {
  yield put(setGlobalLoading(true));
  try {
    const { data } = (yield instance.put(
      `${apiBaseRoutes.MANUFACTURER}${apiRoutes.ASSIGN}/${action.payload.ticketId}/${action.payload?.service?.id}`
    )) as AxiosResponse<ApiResponse<Ticket>>;

    yield put(
      addModal({
        title: i18next.t('ticket_details:ticket_assigned'),
        body: i18next.t('ticket_details:ticket_assigned_details', {
          service: action.payload?.service?.title,
          ticket: getTicketId(data?.data?.prefix, data?.data?.id),
        }),
        modalType: ModalType.SUCCESS,
        icon: CheckCircleBig,
        onBackdropClose: false,
        primaryButtonText: i18next.t('ticket_details:see_details'),
        secondaryButtonText: i18next.t('ticket_details:see_other_tickets'),
        primaryAction: () => {
          store.dispatch(
            replace(`${routes.TICKETS}/${data?.data?.id}`, { forcePush: true })
          );
          store.dispatch(removeModal());
        },
        secondaryAction: () => {
          store.dispatch(replace(routes.TICKETS, { forcePush: true }));
          store.dispatch(removeModal());
        },
      })
    );
  } catch (e) {
    const error = e as CustomAxiosError;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: error.response?.data?.message || Errors.UNKNOWN_ERROR,
        snackKey: Date.now(),
      })
    );
  } finally {
    yield put(setGlobalLoading(false));
  }
}

/* Reassign ticket */
export function* TicketReassignTicketSaga(
  action: ReturnType<typeof reassignTicket>
): Generator {
  yield put(setGlobalLoading(true));
  try {
    const { data } = (yield instance.put(
      `${apiBaseRoutes.MANUFACTURER}${apiRoutes.REASSIGN}/${action.payload.ticketId}/${action.payload?.service?.id}`
    )) as AxiosResponse<ApiResponse<Ticket>>;

    yield put(
      addModal({
        title: i18next.t('ticket_details:ticket_reassigned'),
        body: i18next.t('ticket_details:ticket_reassigned_details', {
          service: `${action.payload?.service?.title} (${action?.payload?.service?.manufacturer?.baseCompany?.title})`,
          ticket: getTicketId(data?.data?.prefix, data?.data?.id),
        }),
        modalType: ModalType.SUCCESS,
        icon: CheckCircleBig,
        onBackdropClose: false,
        primaryButtonText: i18next.t('ticket_details:see_details'),
        secondaryButtonText: i18next.t('ticket_details:see_other_tickets'),
        primaryAction: () => {
          store.dispatch(
            replace(`${routes.TICKETS}/${data?.data?.id}`, { forcePush: true })
          );
          store.dispatch(removeModal());
        },
        secondaryAction: () => {
          store.dispatch(replace(routes.TICKETS, { forcePush: true }));
          store.dispatch(removeModal());
        },
      })
    );
  } catch (e) {
    const error = e as CustomAxiosError;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: error.response?.data?.message || Errors.UNKNOWN_ERROR,
        snackKey: Date.now(),
      })
    );
  } finally {
    yield put(setGlobalLoading(false));
  }
}

/* Get ticket statistics */
export function* TicketGetStatisticsSaga(
  action: ReturnType<typeof getTicketStatistics>
): Generator {
  yield put(showLoading());
  yield put(
    startLoading({ actionType: action.type, id: action.payload?.status })
  );
  try {
    const response = (yield instance.get(
      `${apiBaseRoutes.MANUFACTURER}${apiRoutes.STATS}/${action.payload?.range}/${action.payload?.status}`
    )) as AxiosResponse<ApiResponse<TicketStatistics>>;

    yield put(
      setTicketStatistics({
        status: action.payload.status,
        stats: response?.data?.data,
      })
    );
  } catch (e) {
    const error = e as CustomAxiosError;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: error.response?.data?.message || Errors.UNKNOWN_ERROR,
        snackKey: Date.now(),
      })
    );
  } finally {
    yield put(hideLoading());
    yield put(
      stopLoading({ actionType: action.type, id: action.payload?.status })
    );
  }
}
